import React, { createContext, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { announcementService } from '../../../shared/services/announcement/announcementService';
import type { Announcement, CreateAnnouncementData, UpdateAnnouncementData } from '../types/announcementTypes';


interface AnnouncementContextType {
  // Queries
  announcements: Announcement[];
  isLoading: boolean;
  error: any;
  
  // Mutations
  createAnnouncement: (data: CreateAnnouncementData) => Promise<void>;
  updateAnnouncement: (id: string, data: UpdateAnnouncementData) => Promise<void>;
  deleteAnnouncement: (id: string) => Promise<void>;
  
  // States
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isDeleteModalOpen: boolean;
  
  // Actions
  openDeleteModal: (announcement: Announcement) => void;
  closeDeleteModal: () => void;
  handleDeleteAnnouncement: () => Promise<void>;
  navigateToEdit: (id: string) => void;
  navigateToCreate: () => void;
  navigateToList: () => void;
  
  // Selected
  selectedAnnouncement: Announcement | null;
}

export const AnnouncementContext = createContext<AnnouncementContextType | undefined>(undefined);

interface AnnouncementProviderProps {
  children: ReactNode;
}

export const AnnouncementProvider: React.FC<AnnouncementProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);

  // Query para listar announcements
  const { data: announcements = [], isLoading, error } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => announcementService.list().then(res => res.data.data)
  });

  // Mutation para criar announcement
  const createMutation = useMutation({
    mutationFn: (data: CreateAnnouncementData) => announcementService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      navigate('/announcements');
    }
  });

  // Mutation para atualizar announcement
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAnnouncementData }) => 
      announcementService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      navigate('/announcements');
    }
  });

  // Mutation para deletar announcement
  const deleteMutation = useMutation({
    mutationFn: (id: string) => announcementService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      closeDeleteModal();
    }
  });

  const createAnnouncement = async (data: CreateAnnouncementData) => {
    await createMutation.mutateAsync(data);
  };

  const updateAnnouncement = async (id: string, data: UpdateAnnouncementData) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const deleteAnnouncement = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const openDeleteModal = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedAnnouncement(null);
    navigateToList()
  };

  const handleDeleteAnnouncement = async () => {
    if (selectedAnnouncement) {
      await deleteAnnouncement(selectedAnnouncement.id);
    }
  };

  const navigateToEdit = (id: string) => {
    navigate(`/announcements/${id}/edit`);
  };

  const navigateToCreate = () => {
    navigate('/announcements/new');
  };

  const navigateToList = () => {
    navigate('/announcements');
  };

  const value: AnnouncementContextType = {
    announcements,
    isLoading,
    error,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    handleDeleteAnnouncement,
    navigateToEdit,
    navigateToCreate,
    navigateToList,
    selectedAnnouncement
  };

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
    </AnnouncementContext.Provider>
  );
};